import React, { useEffect, useRef } from 'react';
import { BiCheck } from 'react-icons/bi';

import { SEED_TARGET_EMPTY_MESSAGE } from 'constants/uiConstants';
import type { IInputTask } from 'interfaces/IInputTask';
import MergeClassNames from 'utils/MergeClassNames';

import { getLinkedSeedStepOptions } from './stepCalculations';
import { OPTION_ROW_BASE_CLASSNAME, StepSelectorOption } from './stepConfig';

interface SeedTargetListProps {
  task: IInputTask;
  highlightedStepId: string | null;
  onSelect: (stepId: string | null) => void;
  checkedSeeds?: StepSelectorOption[];
  className?: string;
}

const SeedTargetList: React.FC<SeedTargetListProps> = ({
  task,
  highlightedStepId,
  onSelect,
  checkedSeeds,
  className,
}) => {
  const options = getLinkedSeedStepOptions(task, checkedSeeds);
  const selectedItemRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    selectedItemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [highlightedStepId]);

  return (
    <div
      className={MergeClassNames(
        'flex flex-1 min-h-0 flex-col max-h-[8rem]',
        className
      )}
    >
      <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-gray-bright bg-white">
        {options.length === 0 ? (
          <div className="p-4 text-sm text-danger-dark">
            {SEED_TARGET_EMPTY_MESSAGE}
          </div>
        ) : (
          options.map((option) => {
            const isSelected = option.value === highlightedStepId;
            return (
              <button
                key={option.value}
                ref={isSelected ? selectedItemRef : undefined}
                type="button"
                className={MergeClassNames(
                  OPTION_ROW_BASE_CLASSNAME,
                  isSelected
                    ? 'bg-success-light text-charcoal'
                    : 'bg-white text-charcoal'
                )}
                onClick={() => onSelect(isSelected ? null : option.value)}
              >
                <div
                  className={MergeClassNames(
                    'flex-1 text-base leading-5 text-charcoal',
                    isSelected && 'font-semibold'
                  )}
                >
                  {option.label}
                </div>
                {isSelected && (
                  <BiCheck
                    className="flex-shrink-0 text-lg text-success-dark"
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default SeedTargetList;
